import React, { useId } from "react";
import { Box, Dialog, IconButton, Typography, useTheme } from "@mui/material";
import { CloseRounded as CloseIcon } from "@mui/icons-material";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { overlaySurface, panelBorder } from "../Dashboard/overviewPanel";
import BottomSheet from "./BottomSheet";

// The one dialog-button family. Pointer: a compact 32px pair; touch: a full-
// width 48px stack with the primary on top. Callers pass `fullWidth={touch}`.
export const dialogPrimaryButtonSx = (isDark: boolean, touch: boolean) =>
  ({
    order: touch ? 0 : 1,
    height: touch ? 48 : 32,
    minWidth: touch ? 0 : 72,
    px: 1.75,
    fontSize: touch ? "0.9375rem" : "0.8125rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: touch ? 2 : 1.5,
    backgroundColor: "#22c55e",
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#16a34a", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "rgba(34, 197, 94, 0.15)",
      color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
    },
  }) as const;

export const dialogQuietButtonSx = (isDark: boolean, touch: boolean) =>
  ({
    order: touch ? 1 : 0,
    height: touch ? 48 : 32,
    minWidth: 0,
    px: 1.25,
    fontSize: touch ? "0.9375rem" : "0.8125rem",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: touch ? 2 : 1.5,
    color: "text.secondary",
    "&:hover": {
      color: "text.primary",
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    },
  }) as const;

// A quiet square icon button for dialog headers: colour and a faint wash on
// hover, never a circle.
export const headerIconButtonSx = (isDark: boolean) =>
  ({
    width: 32,
    height: 32,
    borderRadius: 1.5,
    color: "text.secondary",
    "&:hover": {
      color: "text.primary",
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
    },
    "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
  }) as const;

interface AdaptiveDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One line under the title saying what this surface is for. */
  subtitle?: string;
  /** Rendered pinned below the content — submit/cancel actions. */
  actions?: React.ReactNode;
  /** A hairline between the heading and the body, for a body of choices. */
  divided?: boolean;
  maxWidth?: "xs" | "sm" | "md";
  children: React.ReactNode;
}

/**
 * A focused task surface — naming, confirming, managing — presented as a
 * centred dialog on pointer and a bottom sheet on touch.
 */
const AdaptiveDialog: React.FC<AdaptiveDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  actions,
  divided = false,
  maxWidth = "sm",
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();
  const titleId = useId();

  const heading = (
    <>
      <Typography
        id={titleId}
        component="h2"
        sx={{
          m: 0,
          fontWeight: 600,
          fontSize: "1.05rem",
          letterSpacing: "-0.01em",
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.8rem",
            lineHeight: 1.5,
            color: theme.palette.text.secondary,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );

  if (isTouch) {
    return (
      <BottomSheet open={open} onClose={onClose}>
        <Box
          sx={{
            mb: 2.5,
            ...(divided && { pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }),
          }}
        >
          {heading}
        </Box>
        {children}
        {actions && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2.5 }}>{actions}</Box>
        )}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            backgroundImage: "none",
            backgroundColor: overlaySurface(isDark),
            border: `1px solid ${panelBorder(isDark)}`,
            boxShadow: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          pt: 3,
          pb: 2.5,
          ...(divided && { mb: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }),
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{heading}</Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          disableRipple
          sx={{ ...headerIconButtonSx(isDark), flex: "none", mt: -0.5, mr: -0.75 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: actions ? 0 : 3 }}>{children}</Box>

      {actions && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, pt: 2.5, pb: 3 }}>
          {actions}
        </Box>
      )}
    </Dialog>
  );
};

export default AdaptiveDialog;
